import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';
import { getDeliveryLocations } from '@/api/sales/deliveryLocation';
import { getCampaigns } from '@/api/core/campaign';
import { getOrganisations } from '@/api/core/organisation';
import { registerQueueHandler, enqueueOperation, removeQueuedOperations } from '@/sync/offlineSyncQueue';
import { eventBus } from '@/utils/core/eventBus';
import { getActiveLocale } from '@/i18n';

// Returned when offline with nothing cached yet (e.g. first-ever launch offline) — a neutral
// empty state, not placeholder data, since it can be shown to real users.
const EMPTY_POINTS = {
	total: 0,
	pending: 0,
	validated: 0,
	denied: 0,
	organization: null,
	rank: null,
	totalUsers: null,
	nextRank: null,
	season: null,
};

const CLAIM_STATUS = { 0: 'PENDING', 1: 'VALIDATED', 2: 'DENIED' };

export const POINTS_STEP = 0.1;
export const POINTS_DECIMALS = 1;

export function roundPoints(value) {
	const n = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);
	if (!Number.isFinite(n)) return 0;
	const factor = 10 ** POINTS_DECIMALS;
	return Math.round(n * factor) / factor;
}

export function limitPointsDecimals(value) {
	const match = String(value ?? '').match(/^(-?\d*[.,]\d)\d+$/);
	return match ? match[1] : value;
}

export function formatPoints(value) {
	return new Intl.NumberFormat(getActiveLocale(), { maximumFractionDigits: POINTS_DECIMALS }).format(roundPoints(value));
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const pointsCache = new CacheManager('VueCache', 'cache', {
	maxAge: 5 * 60 * 1000,
	memoryCacheTTL: 5 * 60 * 1000,
	logCategory: 'Points',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.points.`,
});

async function apiFetch(endpoint, options = {}) {
	const response = await authenticatedFetch(getApiUrl(endpoint), options, 'tokenSales');
	if (response.status === 404) return null;
	if (!response.ok) throw new Error(`HTTP ${response.status}`);
	return response.json();
}

async function getPeriodState(forceRefresh = false) {
	return pointsCache.fetchOrCache('currentPeriod', async () => ({ period: await apiFetch('Core/Scores/Periods/Current') }), { forceRefresh, fallback: { period: null, unknown: true } });
}

export async function isScoringPeriodClosed() {
	const { period, unknown } = await getPeriodState();
	return !period && !unknown;
}

function daysRemaining(endDate) {
	if (!endDate) return null;
	return Math.max(0, Math.round((new Date(endDate) - new Date()) / 86400000));
}

async function resolveOrganization() {
	const organisationId = iframeDataBridge.getCurrentUser()?.organisationId;
	if (!organisationId) return null;

	const organisations = await getOrganisations().catch(() => []);
	const own = (organisations ?? []).find((o) => o.id === organisationId);
	if (!own) return null;

	const parent = organisations.find((o) => o.id === own.parentId);
	return { parent: parent?.label ?? null, own: own.label ?? null };
}

function periodName(period) {
	if (!period) return null;
	const fmt = new Intl.DateTimeFormat('fr-FR', { month: 'short' });
	const start = new Date(period.startDate);
	const end = new Date(period.endDate);
	return `${fmt.format(start)} – ${fmt.format(end)} ${end.getFullYear()}`;
}

function resolvePromoName(claim, campaignsMap) {
	const campaign = campaignsMap.get(claim.campaignId);
	if (!campaign) return claim.comment?.trim() || `Réclamation #${claim.id}`;
	const offer = claim.campaignOfferId ? campaign.offers?.find((o) => o.id === claim.campaignOfferId) : null;
	return offer ? `${campaign.label} / ${offer.label}` : campaign.label;
}

function mapClaim(claim, dlMap = new Map(), campaignsMap = new Map()) {
	const status = CLAIM_STATUS[claim.status] ?? 'PENDING';
	const dl = dlMap.get(claim.deliveryLocationId);
	const locationLabel = claim.deliveryLocationId ? dl?.companyName || dl?.label || `PDL #${claim.deliveryLocationId}` : null;
	return {
		id: claim.id,
		date: claim.createdDate,
		type: claim.appointmentUid ? 'NEGOTIATION' : 'BONUS',
		campaignId: claim.campaignId,
		offerId: claim.campaignOfferId,
		appointmentUid: claim.appointmentUid,
		deliveryLocationId: claim.deliveryLocationId,
		promoName: resolvePromoName(claim, campaignsMap),
		location: locationLabel,
		pointsClaimed: claim.points,
		pointsValidated: status === 'VALIDATED' ? (claim.pointsValidated ?? null) : null,
		campaignOfferStartDate: claim.campaignOfferStartDate ?? null,
		campaignOfferEndDate: claim.campaignOfferEndDate ?? null,
		status,
		comment: claim.comment ?? '',
		deniedReason: status === 'DENIED' ? claim.comment?.trim() || null : null,
		validator: null,
		_raw: claim,
	};
}

// The legacy VM always builds these dates via `new Date(x).toISOString()` (millisecond precision,
// e.g. "...T23:00:00.000Z"), while the backend's own claim records often omit the milliseconds
// (e.g. "...T23:00:00Z") — same instant, different string. Comparing as strings made every
// classic-offer claim silently fail to match its own offer context. Compare by timestamp instead.
function sameInstant(a, b) {
	const ta = new Date(a).getTime();
	const tb = new Date(b).getTime();
	return !Number.isNaN(ta) && !Number.isNaN(tb) && ta === tb;
}

// Does a points-history entry belong to the given offer context? Every non-null field on
// `context` must match the entry. Catalog offers are identified by campaignId/offerId alone,
// but "pirate" (local, non-catalog) offers can share the same campaignId/offerId across
// distinct negotiations, so the negotiated period (campaignOfferStartDate/EndDate) is also
// compared whenever the context provides it, disambiguating those cases too.
export function matchesOfferContext(entry, context) {
	if (!context) return true;
	const { campaignId, offerId, campaignOfferStartDate, campaignOfferEndDate, deliveryLocationId } = context;

	if (campaignId != null && String(entry.campaignId ?? '') !== String(campaignId)) return false;
	if (offerId != null && String(entry.offerId ?? '') !== String(offerId)) return false;
	if (campaignOfferStartDate && !sameInstant(entry.campaignOfferStartDate, campaignOfferStartDate)) return false;
	if (campaignOfferEndDate && !sameInstant(entry.campaignOfferEndDate, campaignOfferEndDate)) return false;
	if (deliveryLocationId != null && String(entry.deliveryLocationId ?? '') !== String(deliveryLocationId)) return false;
	return true;
}

export async function getUserPoints(forceRefresh = false) {
	return pointsCache.fetchOrCache(
		'userPoints',
		async () => {
			const userId = iframeDataBridge.getCurrentUser()?.id;
			const claimsPath = userId ? `Core/Scores/Claims?userId=${userId}` : 'Core/Scores/Claims';
			const [score, { period, unknown: periodUnknown }, claims, organization] = await Promise.all([apiFetch('Core/Scores/Current'), getPeriodState(forceRefresh), apiFetch(claimsPath), resolveOrganization()]);

			if (!period && !periodUnknown) return { ...EMPTY_POINTS, organization, hasActivePeriod: false };

			const allClaims = Array.isArray(claims) ? claims : [];
			const periodClaims = period ? allClaims.filter((c) => c.scorePeriodId === period.id) : allClaims;
			const pending = periodClaims.filter((c) => c.status === 0).reduce((s, c) => s + c.points, 0);
			const validated = periodClaims.filter((c) => c.status === 1).reduce((s, c) => s + (c.pointsValidated ?? 0), 0);
			const denied = periodClaims.filter((c) => c.status === 2).reduce((s, c) => s + c.points, 0);

			return {
				total: score?.points || validated || 0,
				pending,
				validated,
				denied,
				organization,
				hasActivePeriod: !!period,
				season: period ? { id: period.id, name: periodName(period), daysRemaining: daysRemaining(period.endDate), startDate: period.startDate ?? null, endDate: period.endDate ?? null } : null,
			};
		},
		{ forceRefresh, fallback: EMPTY_POINTS }
	);
}

export async function getPointsHistory(forceRefresh = false) {
	return pointsCache.fetchOrCache(
		'history',
		async () => {
			const userId = iframeDataBridge.getCurrentUser()?.id;
			const claimsPath = userId ? `Core/Scores/Claims?userId=${userId}` : 'Core/Scores/Claims';
			const [claims, deliveryLocations, { period, unknown: periodUnknown }, campaigns] = await Promise.all([apiFetch(claimsPath), getDeliveryLocations().catch(() => []), getPeriodState(forceRefresh), getCampaigns().catch(() => [])]);

			if (!period && !periodUnknown) return [];

			const dlMap = new Map((deliveryLocations ?? []).map((dl) => [dl.id, dl]));
			const campaignsMap = new Map((campaigns ?? []).map((c) => [c.id, c]));
			const allClaims = Array.isArray(claims) ? claims : [];
			const periodClaims = period ? allClaims.filter((c) => c.scorePeriodId === period.id) : allClaims;
			return periodClaims.map((c) => mapClaim(c, dlMap, campaignsMap));
		},
		{ forceRefresh, fallback: [] }
	);
}

export async function claimPoints({ campaignId, offerId, appointmentUid, deliveryLocationId, pointsClaimed, comment, campaignOfferStartDate, campaignOfferEndDate }) {
	if (!iframeDataBridge.isOnline()) throw new Error('offline');

	const { period } = await getPeriodState(true);
	if (!period) throw new Error('NO_ACTIVE_PERIOD');

	const userId = iframeDataBridge.getCurrentUser()?.id;
	const body = {
		userId,
		scorePeriodId: period.id,
		campaignId: campaignId ?? null,
		campaignOfferId: offerId ?? null,
		appointmentUid: appointmentUid ?? null,
		deliveryLocationId: deliveryLocationId ?? null,
		points: roundPoints(pointsClaimed),
		comment: comment ?? '',
		status: 0,
		campaignOfferStartDate: campaignOfferStartDate ?? null,
		campaignOfferEndDate: campaignOfferEndDate ?? null,
	};

	log('info', 'Points', `🎯[CLAIM-DEBUG] POST Core/Scores/Claims — ${pointsClaimed}pts campaignId=${campaignId} offerId=${offerId} apptUid=${appointmentUid ?? 'null'}`);
	const response = await authenticatedFetch(getApiUrl('Core/Scores/Claims'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }, 'tokenSales');
	if (!response.ok) throw new Error(`HTTP ${response.status}`);

	await refreshPoints();
	log('success', 'Points', `🎯[CLAIM-DEBUG] ✅ Claimed ${pointsClaimed} pts (HTTP ${response.status})`);
	return await response.json();
}

export async function updatePointsClaim({ claimId, pointsClaimed, comment }) {
	try {
		if (!iframeDataBridge.isOnline()) throw new Error('offline');

		const current = await apiFetch(`Core/Scores/Claims/${claimId}`);
		if (!current) throw new Error(`Claim ${claimId} not found`);

		const body = { ...current, points: roundPoints(pointsClaimed), comment: comment ?? '' };

		const response = await authenticatedFetch(getApiUrl('Core/Scores/Claims'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP ${response.status}`);

		await refreshPoints();
		log('success', 'Points', `✅ Updated claim ${claimId} to ${pointsClaimed} pts`);
		return await response.json();
	} catch (error) {
		log('warn', 'Points', `⚠️ updatePointsClaim fallback: ${error.message}`);
		return { success: true, pointsClaimed };
	}
}

export async function refreshPoints() {
	pointsCache.clearMemory();
	await pointsCache.clear();
	// Notify already-mounted consumers (offer recaps, widget badge) that the cache was just
	// invalidated — without this, a zone that fetched history before this claim/update still
	// shows its stale one-shot snapshot forever, since it has no other way to learn data changed.
	window.dispatchEvent(new CustomEvent('points-updated'));
}

// --- Deferred claim submission (RG #22908) ---
// PointsClaimButton stores a claim "intent" here as soon as the user confirms it, keyed by
// store+campaign+offer+period, and only actually submits it once the backing visit is validated
// (legacy 'AppointmentUpdated' -> Vue 'appointments-updated'). Storage lives here rather than in
// the component so a persistent zone (PointsDialogZone) can also reconcile pending claims: closing
// a visit unmounts the non-persistent vue-points-statement-zone before the deferred
// 'appointments-updated' event fires, which would otherwise drop the claim on the floor.
const PENDING_CLAIMS_KEY = 'points-pending-claims';
const submittingKeys = new Set();

// A "real" server uid is a non-empty GUID that is neither the empty guid nor a local
// temp uid (negative timestamp string used before the appointment is synced).
export function isRealUid(uid) {
	if (!uid) return false;
	const s = String(uid);
	return s !== EMPTY_GUID && !s.startsWith('-');
}

function pendingKey(ctx) {
	return `${ctx.deliveryLocationId}:${ctx.campaignId}:${ctx.offerId ?? 'null'}:${ctx.campaignOfferStartDate ?? 'null'}:${ctx.campaignOfferEndDate ?? 'null'}`;
}

function loadPendingClaims() {
	try {
		return JSON.parse(localStorage.getItem(PENDING_CLAIMS_KEY) || '{}');
	} catch {
		return {};
	}
}

function saveAllPendingClaims(all) {
	localStorage.setItem(PENDING_CLAIMS_KEY, JSON.stringify(all));
}

export function getPendingClaim(ctx) {
	return loadPendingClaims()[pendingKey(ctx)] ?? null;
}

export function savePendingClaim(ctx, intent) {
	const all = loadPendingClaims();
	all[pendingKey(ctx)] = intent;
	saveAllPendingClaims(all);
}

export function removePendingClaim(ctx) {
	const all = loadPendingClaims();
	delete all[pendingKey(ctx)];
	saveAllPendingClaims(all);
}

// Records that the visit backing this claim was closed. Only a claim carrying this mark may be
// submitted outside the 'appointments-updated' event itself.
export function markPendingClaimValidated(ctx) {
	const all = loadPendingClaims();
	const key = pendingKey(ctx);
	const pending = all[key];
	if (!pending) return null;
	if (!pending.validated) {
		pending.validated = true;
		saveAllPendingClaims(all);
	}
	return pending;
}

// A visit deleted or cancelled before validation never fires 'appointments-updated', so a points
// claim made mid-visit would otherwise sit in localStorage forever and get swept up by
// reconcilePendingClaims the next time ANY visit for the same store is validated (reconciliation
// only matches by store — see reconcilePendingClaims below). Discard every pending claim for that
// store as soon as the legacy app confirms one of its visits was deleted/cancelled (see
// iframeWatcher's 'appointment-deleted').
export async function purgePendingClaimsForStore(deliveryLocationId) {
	if (deliveryLocationId == null) return;
	const all = loadPendingClaims();
	let changed = false;
	for (const [key, pending] of Object.entries(all)) {
		if (String(pending.deliveryLocationId) === String(deliveryLocationId)) {
			delete all[key];
			changed = true;
			log('info', 'Points', `🗑️ Discarded pending claim ${key} — its visit at store ${deliveryLocationId} was deleted`);
		}
	}
	if (changed) saveAllPendingClaims(all);

	// A claim made offline has already left localStorage for the offline queue, where the purge
	// above can't reach it — it would still be submitted on reconnect for a visit that no longer
	// exists.
	try {
		const dropped = await removeQueuedOperations('POINTS_CLAIM', (payload) => String(payload?.deliveryLocationId) === String(deliveryLocationId));
		if (dropped) log('info', 'Points', `🗑️ Discarded ${dropped} queued claim(s) for store ${deliveryLocationId} — its visit was deleted`);
	} catch (error) {
		log('warn', 'Points', `⚠️ Could not purge queued claims for store ${deliveryLocationId}: ${error.message}`);
	}
}

// Submits (or queues, if offline) a single pending claim intent. Guarded by `submittingKeys` so
// the still-mounted button's own listener and the persistent zone's reconciliation pass can't
// both fire the same claim twice.
export async function submitPendingClaim(pending, uid) {
	const key = pendingKey(pending);
	if (submittingKeys.has(key)) return;
	submittingKeys.add(key);

	try {
		const payload = {
			campaignId: pending.campaignId,
			offerId: pending.offerId,
			appointmentUid: uid,
			deliveryLocationId: pending.deliveryLocationId,
			pointsClaimed: pending.pointsClaimed,
			comment: pending.comment,
			campaignOfferStartDate: pending.campaignOfferStartDate,
			campaignOfferEndDate: pending.campaignOfferEndDate,
		};
		const label = `Points ${pending.pointsClaimed} pts – ${pending.promoName ?? ''}`;

		// Offline: defer via offlineSyncQueue so it's submitted automatically once we're back
		// online (SYNC_COMPLETED processes the queue).
		if (!iframeDataBridge.isOnline()) {
			await enqueueOperation('POINTS_CLAIM', payload, label);
			removePendingClaim(pending);
			log('info', 'Points', `📥 Claim queued for sync: ${pending.pointsClaimed} pts`);
			return;
		}

		// Online: submit right away even without a real appointment uid yet — the freshly-closed
		// visit's server-side uid is only assigned by a later full background sync (not by
		// visit-close itself), so waiting for it here would strand the claim for an unbounded time.
		// claimPoints() accepts a null appointmentUid (recorded as a BONUS-type claim instead of a
		// NEGOTIATION one) rather than silently blocking submission (RG #22908).
		await claimPoints(payload);
		removePendingClaim(pending);
		log('success', 'Points', `✅ Submitted deferred claim: ${pending.pointsClaimed} pts`);
	} finally {
		submittingKeys.delete(key);
	}
}

// Safety net for the persistent points-dialog zone: re-submits every locally pending claim on
// 'appointments-updated', regardless of whether the button that originally created it is still
// mounted. Failures are left in localStorage for the next reconciliation pass (mount-time retry
// in PointsClaimButton, or the next 'appointments-updated') rather than silently dropped.
export async function reconcilePendingClaims(data) {
	const evtDeliveryLocationId = data?.deliveryLocationId;
	const all = Object.entries(loadPendingClaims());
	log('info', 'Points', `🎯[CLAIM-DEBUG] reconcilePendingClaims — ${all.length} pending in localStorage, evtDlId=${evtDeliveryLocationId ?? 'none'}, keys=[${all.map(([k]) => k).join(' | ')}]`);
	for (const [key, pending] of all) {
		if (evtDeliveryLocationId != null && String(pending.deliveryLocationId) !== String(evtDeliveryLocationId)) {
			log('info', 'Points', `🎯[CLAIM-DEBUG] skipped ${key} — dlId ${pending.deliveryLocationId} ≠ event ${evtDeliveryLocationId}`);
			continue;
		}
		markPendingClaimValidated(pending);
		const uid = isRealUid(data?.appointmentUid) ? data.appointmentUid : isRealUid(pending.appointmentUid) ? pending.appointmentUid : null;
		log('info', 'Points', `🎯[CLAIM-DEBUG] submitting ${key} — online=${iframeDataBridge.isOnline()} uid=${uid ?? 'null'}`);
		try {
			await submitPendingClaim(pending, uid);
		} catch (error) {
			log('warn', 'Points', `🎯[CLAIM-DEBUG] ⚠️ reconcile submit failed for ${key}: ${error.message}`);
		}
	}
}

registerQueueHandler('POINTS_CLAIM', async (payload) => {
	await claimPoints(payload);
});

eventBus.on('appointment-deleted', (data) => purgePendingClaimsForStore(data?.deliveryLocationId));
